import { fetchWithCredentials } from '../utils/fetchWithCredentials';

const CONTENT_TYPE = 'application/json';

const NOT_FOUND = 404;

export interface AppSessionResponse {
  appSessionId: string;
  app: string;
  creationTime: string;
  lastActivityTime: string;
}

/**
 * Error carrying the HTTP status of a failed app-session call.
 *
 * Consumers need to tell "this app has no session yet" (404) apart from "the
 * service is unavailable" (5xx), because those call for opposite responses:
 * create a session, versus leave the user alone. A plain `Error` forces callers
 * to parse the message, so the status is kept structured here.
 */
export class AppSessionApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'AppSessionApiError';
    this.status = status;
  }
}

/**
 * True when a call failed because no session exists yet for the app — the
 * signal to create one, rather than to treat it as a service failure.
 */
export const isNotFound = (error: unknown): error is AppSessionApiError =>
  error instanceof AppSessionApiError && error.status === NOT_FOUND;

/**
 * Get existing application session
 * GET {endpoint}?appCode={appCode}
 */
export async function getAppSession(
  endpoint: string,
  appCode: string
): Promise<AppSessionResponse> {
  const url = new URL(endpoint, window.location.origin);
  url.searchParams.set('appCode', appCode);

  const response = await fetchWithCredentials(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': CONTENT_TYPE,
    },
  });

  if (!response.ok) {
    throw new AppSessionApiError(
      response.status,
      `Failed to get app session: ${response.status} ${response.statusText}`
    );
  }

  return response.json() as Promise<AppSessionResponse>;
}

/**
 * Create new application session
 * POST {endpoint}
 */
export async function createAppSession(
  endpoint: string,
  appCode: string
): Promise<AppSessionResponse> {
  const response = await fetchWithCredentials(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': CONTENT_TYPE,
    },
    body: JSON.stringify({
      appSessionId: '00000000-0000-0000-0000-000000000000',
      app: appCode,
    }),
  });

  if (!response.ok) {
    throw new AppSessionApiError(
      response.status,
      `Failed to create app session: ${response.status} ${response.statusText}`
    );
  }

  return response.json() as Promise<AppSessionResponse>;
}

/**
 * Extend application session duration
 * PATCH {endpoint}?appCode={appCode}
 * Returns 204 No Content on success
 */
export async function extendAppSession(
  endpoint: string,
  appCode: string
): Promise<void> {
  const url = new URL(endpoint, window.location.origin);
  url.searchParams.set('appCode', appCode);

  const response = await fetchWithCredentials(url.toString(), {
    method: 'PATCH',
    headers: {
      'Content-Type': CONTENT_TYPE,
    },
  });

  if (!response.ok) {
    throw new AppSessionApiError(
      response.status,
      `Failed to extend app session: ${response.status} ${response.statusText}`
    );
  }
}

/**
 * Logout application session
 * DELETE {endpoint}?appCode={appCode}
 * Returns 204 No Content on success
 */
export async function logoutAppSession(
  endpoint: string,
  appCode: string
): Promise<void> {
  const url = new URL(endpoint, window.location.origin);
  url.searchParams.set('appCode', appCode);

  const response = await fetchWithCredentials(url.toString(), {
    method: 'DELETE',
    headers: {
      'Content-Type': CONTENT_TYPE,
    },
  });

  if (!response.ok) {
    throw new AppSessionApiError(
      response.status,
      `Failed to logout app session: ${response.status} ${response.statusText}`
    );
  }
}
